/**
 * Pagination dots for the mobile product image slider (mobile-slider mode).
 *
 * The slider itself is native CSS scroll-snap. This wires the server-rendered
 * dots to it: the active dot follows the scroll position (IntersectionObserver),
 * and tapping a dot scrolls to that image. Only active on the mobile breakpoint;
 * disabled on desktop where the dots are hidden.
 */
(() => {
  const MQ = '(max-width: 749px)';

  class ProductSliderDots {
    constructor(root) {
      this.root = root; // <product-images>
      this.list = root.querySelector('[data-product-media-list]');
      this.dotsWrap = root.querySelector('[data-slider-dots]');
      if (!this.list || !this.dotsWrap) return;

      this.dots = Array.from(this.dotsWrap.querySelectorAll('[data-slider-dot]'));
      this.slides = Array.from(this.list.querySelectorAll('.product__slide'));
      if (this.slides.length < 2) return;

      this.mq = window.matchMedia(MQ);
      this.enabled = false;
      this.onDotClick = this.onDotClick.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onArrowClick = this.onArrowClick.bind(this);
      this.arrows = Array.from(root.querySelectorAll('[data-slider-arrow]'));

      this.sync();
      this.mq.addEventListener('change', this.onModeChange);
    }

    sync() {
      // Mobile breakpoint on the PDP; always on inside the quick-view drawer
      // (which opens at desktop widths but is styled to look like mobile).
      const inQuickView = !!this.root.closest('.product-quick-add');
      (this.mq.matches || inQuickView) ? this.enable() : this.disable();
    }

    enable() {
      if (this.enabled) return;
      this.enabled = true;

      this.dotsWrap.addEventListener('click', this.onDotClick);
      this.arrows.forEach((a) => a.addEventListener('click', this.onArrowClick));

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = this.slides.indexOf(entry.target);
            if (index > -1) this.setActive(index);
          });
        },
        { root: this.list, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    disable() {
      if (!this.enabled) return;
      this.enabled = false;
      this.dotsWrap.removeEventListener('click', this.onDotClick);
      this.arrows.forEach((a) => a.removeEventListener('click', this.onArrowClick));
      this.observer?.disconnect();
    }

    onArrowClick(event) {
      const dir = event.currentTarget.dataset.sliderArrow === 'next' ? 1 : -1;
      // Current slide = the one nearest the current scroll position.
      let current = 0, min = Infinity;
      this.slides.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft - this.list.scrollLeft);
        if (d < min) { min = d; current = i; }
      });
      const target = Math.max(0, Math.min(this.slides.length - 1, current + dir));
      this.list.scrollTo({ left: this.slides[target].offsetLeft, behavior: 'smooth' });
    }

    onModeChange() {
      this.sync();
    }

    onDotClick(event) {
      const button = event.target.closest('[data-slider-dot]');
      if (!button) return;
      const slide = this.slides[parseInt(button.dataset.sliderDot, 10)];
      if (slide) this.list.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }

    setActive(index) {
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }
  }

  const init = () => {
    document
      .querySelectorAll('product-images.product__images--mobile-slider')
      .forEach((el) => {
        if (!el.__sliderDots) el.__sliderDots = new ProductSliderDots(el);
      });
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // The quick view injects its markup via innerHTML (scripts inside don't run),
  // but its <product-info> custom element connects and fires this event — so
  // initialize the injected slider then. init() is idempotent (guarded by
  // el.__sliderDots), so re-running is safe.
  document.addEventListener('theme:product-info:loaded', init);
})();
